import { Component, EventEmitter, Renderer2, forwardRef, OnChanges, AfterViewInit, ViewChild, ElementRef, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from "@angular/forms";
import { SelectItem } from '@app/modules/tf-filters';
import {TfFilterSearchService} from "../tf-filter-search.service";
import { debounceTime, distinctUntilChanged, Subject, Subscription } from "rxjs";

export const MULTI_SELECT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TfMultiSelectComponent),
  multi: true
};

@Component({
  selector: 'tf-multi-select',
  templateUrl: 'tf-multi-select.component.html',
  styleUrls: ['tf-multi-select.component.scss'],
  inputs: ['options', 'defaultLabel', 'scrollHeight', 'optionLabel', 'searchPlaceHolder', 'search', 'virtualScroll', 'disable', 'allowOnlyOne', 'overlayWidth', 'isCondensed', 'sortBy', 'selectedOptions', 'overlayVisible', 'searchInputValue', 'isServerSideSearch', 'isLoadingData', 'hasClearBtn'],
  providers: [MULTI_SELECT_VALUE_ACCESSOR],
  outputs: ['onChange', 'onClick', 'onReset', 'onClosed', 'onSearch']
})
export class TfMultiSelectComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor {

  constructor(public renderer: Renderer2, private searchFilterService: TfFilterSearchService, private cdRef: ChangeDetectorRef) {
  }

  options: SelectItem[] = [];
  defaultLabel: string = "Choose";
  optionLabel: string;
  scrollHeight: number;
  searchPlaceHolder: string;
  search: boolean = false;
  virtualScroll: boolean = false;
  isCondensed: boolean = false;
  disable: boolean = false;
  allowOnlyOne: boolean = false;
  overlayWidth: string = "100%";
  sortBy: 'asc' | 'desc' | 'default' = 'default';
  hasClearBtn: boolean = false;

  onChange: EventEmitter<any> = new EventEmitter();
  onClick: EventEmitter<any> = new EventEmitter();
  onReset: EventEmitter<any> = new EventEmitter();
  onClosed: EventEmitter<any> = new EventEmitter();
  onSearch: EventEmitter<any> = new EventEmitter();
  @ViewChild('toggleButton') toggleButton: ElementRef;
  @ViewChild('filters') filters: ElementRef;

  valueAsString: string;
  showLabel: string = "0 more"
  remainFilters: number = 0;
  visibleOptions: SelectItem[];
  multiSelectOptions: SelectItem[] = [];
  searchInputValue: string;
  isSearchFilter: boolean = false;
  isItemSelected: boolean = false;
  displayCloseIcon: boolean = false;
  overlayVisible: boolean = false;
  documentClickListener: any;
  containerMaxHeight: number = 432;
  selectedOptions: SelectItem[] = [];
  isServerSideSearch: boolean = false;
  searchTextChanged: Subject<string> = new Subject<string>();
  searchTextChangeSubscription: Subscription;
  isLoadingData: boolean = false;

  _onTouched = () => {
      if(!this.overlayVisible) {
          this.searchInputValue = '';
      }
  };

  ngOnInit() {
    this.searchTextChangeSubscription = this.searchTextChanged
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(event => this.handleSearch(event));

      this.searchPlaceHolder = this.searchPlaceHolder || `Search ${this.defaultLabel}`;

  }

  ngOnDestroy() {
    this.searchTextChangeSubscription.unsubscribe();
  }

  ngOnChanges() {
    if(this.options && this.options.length>0){
      this.multiSelectOptions = JSON.parse(JSON.stringify(this.options));
    }else{
      this.multiSelectOptions = [];
    }
    if (this.selectedOptions && this.selectedOptions.length > 0) {
      this.updateSelected();
    } else {
      this.updateLabel();
    }
    if(!this.virtualScroll) {
        this.containerMaxHeight = this.options.length * 54;
    }
  }

  ngAfterViewInit() {
    if (this.overlayVisible) {
      this.show();
    }

    this.cdRef.detectChanges();
  }

  updateLabel = () => {
    if (this.isItemSelected && this.selectedOptions && this.selectedOptions.length > 0) {
      const count: string = (this.selectedOptions.length - 1) > 0 ? (" +" + (this.selectedOptions.length - 1).toString()) : " ";
      this.valueAsString = ( this.optionLabel ? this.selectedOptions[0][this.optionLabel] : this.selectedOptions[0]['label'] ) + count;
    } else {
      this.valueAsString = this.defaultLabel;
    }
  };

  onMouseClick = (event) => {
    if (this.overlayVisible) {
      this.hide();
      this._onTouched();
    } else {
      this.show();
    }
    this.onClick.emit({
      originalEvent: event,
      selectedOptions: this.selectedOptions
    });
  };

  onOptionClick = (event, option) => {
    event.option = option;
    const index = this.findIndexInList(this.visibleOptions, event.option);
    const indexInOptions = this.findIndexInList(this.multiSelectOptions, event.option);
    if (event.option) {
      if (event.option.disabled) {
        return;
      }
      if (this.isItemSelected) {
          const clickedItemIndex: number = this.findIndexInList(this.selectedOptions, event.option);
          if (clickedItemIndex !== -1) {
            this.selectedOptions = this.selectedOptions.filter((val, i) => i != clickedItemIndex);
            this.options.forEach(opt => {
              if(opt['app'] === event.option.app) opt['isChecked'] = false;
            })
            this.visibleOptions[index]['isChecked'] = false;
            this.multiSelectOptions[indexInOptions]['isChecked'] = false;
          } else {
            this.selectedOptions.push(this.options[indexInOptions]);
            this.visibleOptions[index]['isChecked'] = true;
            this.multiSelectOptions[indexInOptions]['isChecked'] = true;
            this.options[indexInOptions]['isChecked'] = true;
          }
          if (this.selectedOptions && this.selectedOptions.length <= 0) {
            this.isItemSelected = false;
          }
      } else {
        this.isItemSelected = true;
        this.selectedOptions.push(this.options[indexInOptions]);
        this.visibleOptions[index]['isChecked'] = true;
        this.multiSelectOptions[indexInOptions]['isChecked'] = true;
        this.options[indexInOptions]['isChecked'] = true;
      }
    }

    const selectedValues = JSON.parse(JSON.stringify(this.selectedOptions));
    this._onChange(selectedValues);
    this.updateLabel();
    this.onChange.emit({
      originalEvent: event.originalEvent,
      selectedOptions: this.selectedOptions,
      clickedOption: this.options[indexInOptions]
    });
  };

  findIndexInList = (list, option) => {
    let index: number = -1;
    let label: string;
    if (this.optionLabel && option && option[this.optionLabel]) {
      label = this.optionLabel;
    } else if (option && option['label']) {
      label = 'label';
    }
      if (list && list.length > 0) {
      if (option && option[label]) {
        for (let i = 0; i < list.length; i++) {
          if (option['code'] === list[i]['code'] && option[label] == list[i][label]) {
            index = i;
            break;
          }
        }
      }
    }
    return index;
  };

  isOptionSelected = (option) => {
    return this.findIndexInList(this.selectedOptions, option) != -1;
  };

  show = () => {
    if (!this.overlayVisible) {
      this.overlayVisible = true;
    }
    this.visibleOptions = this.multiSelectOptions;
    this.displayCloseIcon = false;
    this.bindDocumentClickListener();
  };

  hide = () => {
    this.overlayVisible = false;
    if (this.isSearchFilter) {
        this.filterOptions("");
        this.searchInputValue = '';
    }
    this.unbindDocumentClickListener();
    if (this.selectedOptions && this.selectedOptions.length > 0) {
      this.displayCloseIcon = true;
    }
    this.onClosed.emit({
        originalEvent: null,
        selectedOptions: this.selectedOptions,
        clickedOption: null
    })
  };

  resetDropDown = (event) => {
    event && event.stopPropagation();
    this._onTouched();
    this.resetFilter();
  };

  resetFilter = () => { // set isChecked to false
    this.selectedOptions = [];
    this.isItemSelected = false;
    this.displayCloseIcon = false;
    this.valueAsString = this.defaultLabel;
    this.isSearchFilter = false;
    this.onReset.emit({});
    this._onChange([]);
    this.clearAllCheckBoxesAndFilters();
    this.hide();
  };

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.documentClickListener = this.renderer.listen('document', 'click', (event) => {
        const filterClass = this.filters?.nativeElement.className.split(' ')[0] || 'tf-multi-select-options';
        const matchedElements = document.querySelector(`.${filterClass}`)?.querySelectorAll(`.${event.target.classList[0]}`);

        if (matchedElements?.length === 0 && this.toggleButton.nativeElement !== event.target) {
          this.hide();
          this._onTouched();
        }
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  updateSelected = () => {
    if (!this.selectedOptions || this.selectedOptions.length <= 0) return;
    this.isItemSelected = true;
    this.displayCloseIcon = true;
    for (let i = 0; i < this.selectedOptions.length; i++) {
      const indexInOptions = this.findIndexInList(this.multiSelectOptions, this.selectedOptions[i]);
      if(indexInOptions !== -1) this.multiSelectOptions[indexInOptions]['isChecked'] = this.selectedOptions[i]['isChecked'];
    }
    this.updateLabel();
  }

  getLabel(option) {
    const countLabel = option['value']['count'] ? ` (${option['value']['count']})` : "";

    if (this.optionLabel && option[this.optionLabel])
      return option[this.optionLabel] + countLabel;

    return option.label + countLabel;
  }

  clearAllCheckBoxesAndFilters() {
    for (let i = 0; i < this.multiSelectOptions.length; i++) {
      this.multiSelectOptions[i]['isChecked'] = false;
    }
    this.visibleOptions = this.multiSelectOptions;
  }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _onChange(selectedValues: any) {

  }

  writeValue(value: any): void {

    this.selectedOptions = [];
    this.isItemSelected = false;
    this.displayCloseIcon = false;
    this.valueAsString = this.defaultLabel;
    this.isSearchFilter = false;
    this.clearAllCheckBoxesAndFilters();
    this.hide();

    if (value && value.length > 0) {

      for (let i = 0; i < value.length; i++) {
        if (this.findIndexInList(this.multiSelectOptions, value[i]) > -1) {
          this.selectedOptions.push(value[i]);
        }
      }
    }

    this.updateSelected();

    const selectedValues = JSON.parse(JSON.stringify(this.selectedOptions));
    this.onChange.emit({
      originalEvent: null,
      selectedOptions: selectedValues,
      clickedOption: null
    });
  }

  registerOnChange(fn: (selectedOptions: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disable = val;
  }

  onOptionWrapperClick(event, option) {
    this.onOptionClick({
      originalEvent: event,
      option: option
    }, option);
  }

  filterOptions(event: string) : void {
    this.isLoadingData = true;
    this.searchTextChanged.next(event);
  }

  onCloseFilters() {
    this.hide();
    this._onTouched();
  }

  handleSearch(event) {
    if(!this.isServerSideSearch) {
      const searchLabel: string = this.optionLabel && this.optionLabel.length > 0 ? this.optionLabel : 'label';
      if (event && event.length > 0) {
        this.isSearchFilter = true;
        this.visibleOptions = this.searchFilterService.filterBySearch(this.multiSelectOptions, searchLabel, event);
      } else {
        this.isSearchFilter = false;
        this.visibleOptions = this.multiSelectOptions;
      }
      this.isLoadingData = false;
    }

    this.onSearch.emit(event);
  }

    onClearKeyword() {
      if(this.searchInputValue && this.searchInputValue !== "") {
          this.isLoadingData = true;
          this.searchInputValue = "";
          this.searchTextChanged.next("");
      }
    }

    isEmpty() {
      return this.visibleOptions?.length === 0;
    }
}
